import type { AcademyAreaContent, AcademySkillContent } from './types';

const tiaTeacherGuideUrl =
  'https://exploros-shared-media.s3.us-east-1.amazonaws.com/Site/Exploros+TIA+Social+Studies+Teacher+Guide.pdf';
const tiaTestBlueprintUrl =
  'https://exploros-shared-media.s3.us-east-1.amazonaws.com/Site/Exploros-Social-Studies-Pre-and-Post-Test-Blueprints.pdf';
const tiaAcademyVideoUrls = {
  overview: 'https://exploros-shared-media.s3.us-east-1.amazonaws.com/Academy/Getting-to-Know-the-Pre-Post-Tests.mp4',
  planPrepare: 'https://exploros-shared-media.s3.us-east-1.amazonaws.com/Academy/Plan-and-Prepare-for-Testing.mp4',
  setupClasses: 'https://exploros-shared-media.s3.us-east-1.amazonaws.com/Academy/Set-Up-Your-Classes-for-Testing.mp4',
  previewDirections: 'https://exploros-shared-media.s3.us-east-1.amazonaws.com/Academy/Prepare-for-a-Smooth-Testing-Experience.mp4',
  assignTest: 'https://exploros-shared-media.s3.us-east-1.amazonaws.com/Academy/Assign-a-Test.mp4',
  startMonitorEnd:
    'https://exploros-shared-media.s3.us-east-1.amazonaws.com/Academy/Start-Monitor-and-End-a-Pre-or-Post-Test.mp4',
  reviewPreResults:
    'https://exploros-shared-media.s3.us-east-1.amazonaws.com/Academy/Review-Your-Pre-Test-Results.mp4',
  instructionalPlanning:
    'https://exploros-shared-media.s3.us-east-1.amazonaws.com/Academy/Teach-All-Year-with-Purpose.mp4',
} as const;

export const academyAreaContent: AcademyAreaContent[] = [
  {
    "academyAreaKey": "pre-post-tia",
    "planToken": "tia",
    "label": "Pre and Post Tests (TIA)",
    "heading": "Exploros Academy - Pre and Post Testing",
    "description": "Learn how to administer Pre and Post Tests and check results.",
    "metrics": [
      {
        "metricKey": "licensed_students",
        "label": "Licensed Students"
      },
      {
        "metricKey": "pre_tests_completed",
        "label": "Pre-Tests Completed"
      },
      {
        "metricKey": "post_tests_completed",
        "label": "Post-Tests Completed"
      }
    ],
    "tiers": [
      {
        "tierKey": "assessment_facilitator",
        "step": 1,
        "name": "Assessment Facilitator",
        "description": "Administer tests to establish growth data"
      },
      {
        "tierKey": "growth_planner",
        "step": 2,
        "name": "Growth Planner",
        "description": "Review test results and identify TEKS for growth"
      }
    ],
    "skillPaths": [
      {
        "pathKey": "plan_prepare",
        "title": "Plan & Prepare",
        "displayOrder": 1
      },
      {
        "pathKey": "teach_facilitate",
        "title": "Teach & Facilitate",
        "displayOrder": 2
      },
      {
        "pathKey": "assess_adjust",
        "title": "Assess & Adjust",
        "displayOrder": 3
      }
    ]
  },
  {
    "academyAreaKey": "social-studies-tx",
    "planToken": "ss-tx-gold",
    "label": "Social Studies (TX)",
    "heading": "Exploros Academy - Social Studies (TX)",
    "description": "Learn how to utilize Exploros for social studies instruction.",
    "metrics": [
      {
        "metricKey": "licensed_students",
        "label": "Licensed Students"
      },
      {
        "metricKey": "experiences_assigned",
        "label": "Experiences Assigned"
      },
      {
        "metricKey": "activities_reviewed",
        "label": "Activities Reviewed"
      }
    ],
    "tiers": [
      {
        "tierKey": "classroom_practitioner",
        "step": 1,
        "name": "Classroom Practitioner",
        "description": "Launch experiences and use core instructional practices with students."
      },
      {
        "tierKey": "engagement_leader",
        "step": 2,
        "name": "Engagement Leader",
        "description": "Deeply facilitate experiences, discussions, differentiation, and data use."
      }
    ],
    "skillPaths": [
      {
        "pathKey": "plan_prepare",
        "title": "Plan & Prepare",
        "displayOrder": 1
      },
      {
        "pathKey": "teach_facilitate",
        "title": "Teach & Facilitate",
        "displayOrder": 2
      },
      {
        "pathKey": "assess_adjust",
        "title": "Assess & Adjust",
        "displayOrder": 3
      }
    ]
  },
  {
    "academyAreaKey": "digital-citizenship",
    "planToken": "dc",
    "label": "Digital Citizenship",
    "heading": "Exploros Academy - Digital Citizenship",
    "description": "Learn how to support responsible digital citizenship instruction.",
    "metrics": [
      {
        "metricKey": "licensed_students",
        "label": "Licensed Students"
      },
      {
        "metricKey": "experiences_assigned",
        "label": "Experiences Assigned"
      },
      {
        "metricKey": "activities_reviewed",
        "label": "Activities Reviewed"
      }
    ],
    "tiers": [],
    "skillPaths": [
      {
        "pathKey": "plan_prepare",
        "title": "Plan & Prepare",
        "displayOrder": 1
      },
      {
        "pathKey": "teach_facilitate",
        "title": "Teach & Facilitate",
        "displayOrder": 2
      },
      {
        "pathKey": "assess_adjust",
        "title": "Assess & Adjust",
        "displayOrder": 3
      }
    ]
  }
];

export const academySkillContent: AcademySkillContent[] = [
  {
    "skillKey": "getting-to-know-pre-post-tests",
    "title": "Overview of the Pre & Post Tests",
    "description": "Understand the role of the Pre- and Post-Tests and the essential details needed for test administration.",
    "learnType": "video",
    "learnContent": {
      "contentType": "video",
      "title": "Overview of the Pre & Post Tests",
      "contentPath": "skill-content/getting-to-know-pre-post-tests",
      "sourceUrl": tiaAcademyVideoUrls.overview,
      "body": "**Welcome to Exploros Academy!** In this course, you'll learn everything you need to successfully administer the Exploros Texas Social Studies Pre- and Post-Tests.\n\nLet's get started with the first lesson, **Overview of the Pre & Post Tests**.",
      "estimatedMinutes": 3,
      "details": {
        "intro": "**Welcome to Exploros Academy!** In this course, you'll learn everything you need to successfully administer the Exploros Texas Social Studies Pre- and Post-Tests.\n\nLet's get started with the first lesson, **Overview of the Pre & Post Tests**.",
        "resources": [
          {
            "label": "Chapter 1: Overview of the Test in the Teacher Guide",
            "url": tiaTeacherGuideUrl
          },
          {
            "label": "Pre-Post Test Blueprint",
            "url": tiaTestBlueprintUrl
          }
        ],
      }
    },
    "appliedActions": [
      "Review the assessment cycle",
      "Review the testing chain"
    ],
    "confirmationPolicy": "user_full_confirm",
    "selfReportPrompt": "I understand the purpose of the Pre- and Post-Tests, the overall assessment process, and my responsibilities as the test administrator."
  },
  {
    "skillKey": "set-up-classes-for-testing",
    "title": "Set Up Classes and Licenses",
    "description": "Set up classes and assign licenses using the appropriate rostering method.",
    "learnType": "video",
    "learnContent": {
      "contentType": "video",
      "title": "Check Rosters and Licenses Video",
      "contentPath": "skill-content/set-up-classes-for-testing",
      "sourceUrl": tiaAcademyVideoUrls.setupClasses,
      "body": "In this lesson, we will review how to set up your classes and assign licenses, including the available rostering options, how licenses are assigned to classes, and key considerations for setting up your classes.",
      "estimatedMinutes": 4,
      "details": {
        "intro": "In this lesson, we will review how to set up your classes and assign licenses, including the available rostering options, how licenses are assigned to classes, and key considerations for setting up your classes.",
        "resources": [
          {
            "label": "Chapter 2: Before the Test in the Teacher Guide",
            "url": tiaTeacherGuideUrl
          }
        ]
      }
    },
    "appliedActions": [
      "License class"
    ],
    "confirmationPolicy": "block_on_warning",
    "selfReportPrompt": "Please check your rosters and make sure your classes are licensed. When you have done this, give us the signal."
  },
  {
    "skillKey": "prepare-smooth-testing-experience",
    "title": "Plan and Prepare for Testing",
    "description": "Identify key considerations for planning and preparing for successful test administration.",
    "learnType": "video",
    "learnContent": {
      "contentType": "video",
      "title": "Plan and Prepare for Testing",
      "contentPath": "skill-content/prepare-smooth-testing-experience",
      "sourceUrl": tiaAcademyVideoUrls.planPrepare,
      "body": "In this lesson, we will review several important considerations to help you plan and prepare for testing, including understanding your testing windows, determining your rostering method, and developing a testing plan for accommodations, absent students, and other testing needs.",
      "estimatedMinutes": 4,
      "details": {
        "intro": "In this lesson, we will review several important considerations to help you plan and prepare for testing, including understanding your testing windows, determining your rostering method, and developing a testing plan for accommodations, absent students, and other testing needs.",
        "resources": [
          {
            "label": "Chapter 2: Before the Test in the Teacher Guide",
            "url": tiaTeacherGuideUrl
          }
        ]
      }
    },
    "appliedActions": [],
    "confirmationPolicy": "user_full_confirm",
    "selfReportPrompt": "I understand my testing window, my district's rostering method, and how to prepare a testing plan for accommodations, absent students, and other testing needs."
  },
  {
    "skillKey": "preview-test-directions",
    "title": "Preview Test Directions",
    "description": "Identify available resources for previewing test directions and practicing question types.",
    "learnType": "video",
    "learnContent": {
      "contentType": "video",
      "title": "Preview Test Directions",
      "contentPath": "skill-content/preview-test-directions",
      "sourceUrl": tiaAcademyVideoUrls.previewDirections,
      "body": "In this lesson, you'll learn how to prepare students for testing by previewing the test directions, providing opportunities to practice assessment question types, and understanding what assessment content can and cannot be previewed before testing.",
      "estimatedMinutes": 3,
      "details": {
        "intro": "In this lesson, you'll learn how to prepare students for testing by previewing the test directions, providing opportunities to practice assessment question types, and understanding what assessment content can and cannot be previewed before testing.",
        "resources": [
          {
            "label": "Chapter 2: Before the Test in the Teacher Guide",
            "url": tiaTeacherGuideUrl
          }
        ]
      }
    },
    "appliedActions": [
      "Preview a test"
    ],
    "confirmationPolicy": "confirm_on_warning",
    "selfReportPrompt": "Please preview a test. When you have done this, give us the signal."
  },
  {
    "skillKey": "assign-a-test",
    "title": "Assign a Test",
    "description": "Understand the steps for assigning the Pre- or Post-Test to individual students, groups, or classes.",
    "learnType": "video",
    "learnContent": {
      "contentType": "video",
      "title": "Assign a Test",
      "contentPath": "skill-content/assign-a-test",
      "sourceUrl": tiaAcademyVideoUrls.assignTest,
      "body": "In this lesson, we will walk through the steps for assigning the Pre- or Post-Test. You'll learn how to assign the assessment to individual students, groups of students, or entire classes, as well as how to schedule the test for a specific date and time.",
      "estimatedMinutes": 3,
      "details": {
        "intro": "In this lesson, we will walk through the steps for assigning the Pre- or Post-Test. You'll learn how to assign the assessment to individual students, groups of students, or entire classes, as well as how to schedule the test for a specific date and time.",
        "resources": [
          {
            "label": "Chapter 3: Assign, Start, Monitor, and End the Test in the Teacher Guide",
            "url": tiaTeacherGuideUrl
          }
        ]
      }
    },
    "appliedActions": [
      "Assign a test"
    ],
    "confirmationPolicy": "confirm_on_warning",
    "selfReportPrompt": "Please assign your Pre- and Post-Tests in Exploros and schedule them for your planned testing windows. If you schedule your tests for future dates, students will not see them until those dates.\n\nWhen you've finished scheduling your tests, return here and give us the signal."
  },
  {
    "skillKey": "start-monitor-end-pre-post-test",
    "title": "Start, Monitor, and End a Test",
    "description": "Launch a Pre- or Post-Test, monitor student progress, and properly end the test session.",
    "learnType": "video",
    "learnContent": {
      "contentType": "video",
      "title": "Start, Monitor, and End a Test",
      "contentPath": "skill-content/start-monitor-end-pre-post-test",
      "sourceUrl": tiaAcademyVideoUrls.startMonitorEnd,
      "body": "In this lesson, we will walk through how to start, monitor, and end a Pre- or Post-Test. You'll learn how to launch the assessment, track student progress during testing, and properly end the test session once students have completed the test.",
      "estimatedMinutes": 4,
      "details": {
        "intro": "In this lesson, we will walk through how to start, monitor, and end a Pre- or Post-Test. You'll learn how to launch the assessment, track student progress during testing, and properly end the test session once students have completed the test.",
        "resources": [
          {
            "label": "Chapter 3: Assign, Start, Monitor, and End the Test in the Teacher Guide",
            "url": tiaTeacherGuideUrl
          }
        ]
      }
    },
    "appliedActions": [
      "Start a test with students"
    ],
    "confirmationPolicy": "confirm_on_warning",
    "selfReportPrompt": "You're ready to put your learning into practice! Administer the Pre- or Post-Test by starting, monitoring, and ending the assessment with your students.\n\nWhen you've finished testing, return here and give us the signal. We'll unlock **Tier 2 – Growth Planner**, where you'll learn how to review test results and plan for student growth."
  },
  {
    "skillKey": "review-pre-test-results",
    "title": "Review the Pre-Test Results",
    "description": "Access the Pre/Post Test Report and identify the information available within the report.",
    "learnType": "interactive_video",
    "learnContent": {
      "contentType": "interactive_video",
      "title": "Review the Pre-Test Results",
      "contentPath": "skill-content/review-pre-test-results",
      "sourceUrl": tiaAcademyVideoUrls.reviewPreResults,
      "body": "In this lesson, we will review how to access the Pre/Post Test Report and the information it provides. You'll learn where to find the report and how to view student performance, growth, and beginning of year assessment data.",
      "estimatedMinutes": 5,
      "details": {
        "intro": "In this lesson, we will review how to access the Pre/Post Test Report and the information it provides. You'll learn where to find the report and how to view student performance, growth, and beginning of year assessment data.",
        "resources": [
          {
            "label": "Chapter 4: After the Test in the Teacher Guide",
            "url": tiaTeacherGuideUrl
          }
        ]
      }
    },
    "appliedActions": [
      "End a test with student results"
    ],
    "confirmationPolicy": "block_on_warning",
    "selfReportPrompt": "Please end a test with student results. When you have done this, give us the signal."
  },
  {
    "skillKey": "teach-all-year-with-purpose",
    "title": "From Pre-Test to Post-Test: Instructional Planning",
    "description": "Identify instructional planning recommendations for supporting student growth between assessments.",
    "learnType": "video",
    "learnContent": {
      "contentType": "video",
      "title": "From Pre-Test to Post-Test: Instructional Planning",
      "contentPath": "skill-content/teach-all-year-with-purpose",
      "sourceUrl": tiaAcademyVideoUrls.instructionalPlanning,
      "body": "In this lesson, we will share high-level recommendations for instructional planning, including guidance for teachers using the Exploros curriculum and those using other instructional resources.",
      "estimatedMinutes": 4,
      "details": {
        "intro": "In this lesson, we will share high-level recommendations for instructional planning, including guidance for teachers using the Exploros curriculum and those using other instructional resources.",
        "resources": [
          {
            "label": "Chapter 4: After the Test in the Teacher Guide",
            "url": tiaTeacherGuideUrl
          },
          {
            "label": "Test Blueprint",
            "url": tiaTestBlueprintUrl
          }
        ]
      }
    },
    "appliedActions": [],
    "confirmationPolicy": "user_full_confirm",
    "selfReportPrompt": "I understand how to identify instructional planning recommendations for supporting student growth between assessments."
  },
  {
    "skillKey": "preview-learning-experience",
    "title": "Preview a Learning Experience",
    "description": "Review the teacher path and student tasks before assigning an experience.",
    "learnType": "video",
    "learnContent": {
      "contentType": "video",
      "title": "Preview a Learning Experience",
      "contentPath": "skill-content/preview-learning-experience",
      "sourceUrl": "https://exploros-shared-media.s3.amazonaws.com/Help+Videos/Preview-a-Learning-Experience.mp4",
      "body": "Mock video: shows how to inspect teacher flow, student tasks, materials, and pacing notes before assigning an experience.",
      "estimatedMinutes": 4,
      "details": {
        "intro": "Preview a learning experience so the teacher view, student tasks, and pacing notes are clear before class.",
        "goals": [
          "Review the teacher flow before assigning the experience.",
          "Check student tasks and materials.",
          "Notice where pacing decisions may be needed during instruction."
        ],
        "practice": [
          "Open an experience from the library.",
          "Use preview to inspect the student and teacher flow.",
          "Apply the skill after previewing the experience."
        ],
        "sections": [],
        "resources": [],
        "timingNote": null,
        "supportNote": null,
        "checkPrompt": "Academy will look for a learning experience preview event before this Apply step can pass.",
        "transcript": []
      }
    },
    "appliedActions": [],
    "selfReportPrompt": null
  },
  {
    "skillKey": "launch-discussion",
    "title": "Launch a Class Discussion",
    "description": "Use discussion prompts to connect student thinking to the lesson goal.",
    "learnType": "text",
    "learnContent": {
      "contentType": "text",
      "title": "Launch a Class Discussion",
      "contentPath": "skill-content/launch-discussion",
      "sourceUrl": null,
      "body": "Mock reading: gives a short classroom routine for using discussion prompts, student responses, and follow-up questions.",
      "estimatedMinutes": 2,
      "details": {
        "intro": "Use student responses to start a focused class discussion instead of moving through slides too quickly.",
        "goals": [
          "Choose a prompt that invites student thinking.",
          "Use student responses as evidence for the discussion.",
          "Ask a follow-up question that connects back to the lesson goal."
        ],
        "practice": [
          "Pause during an Exploros lesson after students respond.",
          "Select one or two responses to discuss.",
          "Confirm the skill after trying the discussion routine."
        ],
        "sections": [],
        "resources": [],
        "timingNote": null,
        "supportNote": null,
        "checkPrompt": "This Apply step is teacher-confirmed because the system cannot know the quality of the discussion.",
        "transcript": []
      }
    },
    "appliedActions": [],
    "selfReportPrompt": "I used student responses to deepen classroom discussion."
  },
  {
    "skillKey": "review-experience-summary",
    "title": "Review an Experience Summary",
    "description": "Use the summary to plan reteaching and next steps.",
    "learnType": "video",
    "learnContent": {
      "contentType": "video",
      "title": "Review an Experience Summary",
      "contentPath": "skill-content/review-experience-summary",
      "sourceUrl": null,
      "body": "Mock video: shows how to read an experience summary and choose reteaching or extension steps before the next class.",
      "estimatedMinutes": 4,
      "details": {
        "intro": "Review the experience summary to decide what students understood and what needs attention next.",
        "goals": [
          "Find the summary for a completed experience.",
          "Read participation and response patterns.",
          "Use the summary to choose a reteach or extension move."
        ],
        "practice": [
          "Open a completed experience summary.",
          "Identify one strength and one area that needs support.",
          "Confirm that the summary shaped your next teaching decision."
        ],
        "sections": [],
        "resources": [],
        "timingNote": null,
        "supportNote": null,
        "checkPrompt": "Academy can check summary access, then asks for teacher confirmation about the planning decision.",
        "transcript": []
      }
    },
    "appliedActions": [],
    "selfReportPrompt": "I used the summary to choose what to reteach or adjust."
  },
  {
    "skillKey": "reteach-objectives",
    "title": "Review Quiz Results for Reteaching",
    "description": "Use quiz results to choose objectives that need more support.",
    "learnType": "quick_tour",
    "learnContent": {
      "contentType": "quick_tour",
      "title": "Review Quiz Results for Reteaching",
      "contentPath": "skill-content/reteach-objectives",
      "sourceUrl": null,
      "body": "Mock tour: shows where quiz results appear and how to choose objectives that need follow-up support.",
      "estimatedMinutes": 3,
      "details": {
        "intro": "Use quiz results to choose objectives that need reteaching or extra practice.",
        "goals": [
          "Open quiz results for a completed experience.",
          "Find objectives where students need support.",
          "Choose a follow-up step based on the data."
        ],
        "practice": [
          "Review quiz results for one class.",
          "Select an objective that needs more support.",
          "Confirm that the data shaped a reteach choice."
        ],
        "sections": [],
        "resources": [],
        "timingNote": null,
        "supportNote": null,
        "checkPrompt": "Academy can check quiz result access, then asks for teacher confirmation about the reteach decision.",
        "transcript": []
      }
    },
    "appliedActions": [],
    "selfReportPrompt": "I used quiz results to choose objectives for reteaching."
  },
  {
    "skillKey": "facilitate-small-groups",
    "title": "Facilitate Small Group Work",
    "description": "Use small groups to focus students on shared evidence and next steps.",
    "learnType": "text",
    "learnContent": {
      "contentType": "text",
      "title": "Facilitate Small Group Work",
      "contentPath": "skill-content/facilitate-small-groups",
      "sourceUrl": null,
      "body": "Mock reading: outlines a simple routine for grouping students, assigning roles, and bringing evidence back to the full class.",
      "estimatedMinutes": 3,
      "details": {
        "intro": "Use small groups to help students work with evidence, share thinking, and return stronger ideas to the full class.",
        "goals": [
          "Choose a task that benefits from group talk.",
          "Give students a clear role or focus.",
          "Bring the group work back to the lesson goal."
        ],
        "practice": [
          "Pick one experience moment for small group work.",
          "Set a short group task with a clear outcome.",
          "Confirm the skill after trying the routine with students."
        ],
        "sections": [],
        "resources": [],
        "timingNote": null,
        "supportNote": null,
        "checkPrompt": "This Apply step is teacher-confirmed because the system cannot fully judge the classroom routine.",
        "transcript": []
      }
    },
    "appliedActions": [],
    "selfReportPrompt": "I tried small group work during an Exploros lesson."
  },
  {
    "skillKey": "identify-misconceptions",
    "title": "Identify Misconceptions in Real Time",
    "description": "Use live student responses to spot misunderstandings during instruction.",
    "learnType": "interactive_video",
    "learnContent": {
      "contentType": "interactive_video",
      "title": "Identify Misconceptions in Real Time",
      "contentPath": "skill-content/identify-misconceptions",
      "sourceUrl": null,
      "body": "Mock interactive video: shows how to use live student responses and auto-scored signals to decide when to pause and reteach.",
      "estimatedMinutes": 5,
      "details": {
        "intro": "Use live student responses to notice misunderstandings while there is still time to adjust instruction.",
        "goals": [
          "Read student responses during the lesson.",
          "Look for patterns in incorrect or incomplete thinking.",
          "Pause and reteach when the data shows a common misconception."
        ],
        "practice": [
          "Open live response indicators during a lesson.",
          "Identify one pattern that needs attention.",
          "Apply the skill after using live data to adjust instruction."
        ],
        "sections": [],
        "resources": [],
        "timingNote": null,
        "supportNote": null,
        "checkPrompt": "Academy will require system proof that live response indicators were used.",
        "transcript": []
      }
    },
    "appliedActions": [],
    "selfReportPrompt": null
  }
];
